import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Tooltip,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  createVersionDraft,
  deleteArchivedPlanPermanently,
  getAssignableClients,
  duplicatePlan,
  duplicatePlanAsReusable,
  getPlanAssignmentCoverage,
  getPlanTypeLabel,
  listPlans,
  getEditorStagingPresenceByPlanIds,
  setPlanStatus,
  trackTrainingPlansV2Event,
} from "./services/trainingPlansV2Service";
import { normalizePlanLifecycleStatus } from "./services/planLifecycleStatus";
import { withGuidedSearchPreserve } from "./trainingPlansV2GuidedTaskParams";
import { useTrainingPlansV2Hints } from "./useTrainingPlansV2Hints";
import { normalizeFirestoreDate } from "../../../../lib/firestoreDate";
import useLabels from "../../../../hooks/useLabels";
import LabelChips from "../../../../components/molecules/LabelChips";

const SCOPES = ["template", "client"];
/** Default: drafts + published active, drafts listed first in the table. */
const DEFAULT_VERSION_STATUS = "in_progress";
const VERSION_STATUS_IDS = [
  "in_progress",
  "active",
  "draft",
  "archived",
  "all",
];
const SORTS = ["updated_desc", "name_asc", "version_desc", "version_asc"];
/** Firestore limits `array-contains-any` to 10 comparison values */
const MAX_LIBRARY_PROTOCOL_LABEL_FILTERS = 10;

function parseLabelsQueryParam(raw) {
  return String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX_LIBRARY_PROTOCOL_LABEL_FILTERS);
}

const normalizeFilterValue = (value, allowedValues, fallback) =>
  allowedValues.includes(value) ? value : fallback;

const getCoverageChipSx = (count, activeColor) =>
  count > 0
    ? (theme) => ({
        color: theme.palette[activeColor].main,
        borderColor: theme.palette[activeColor].main,
        backgroundColor: alpha(theme.palette[activeColor].main, 0.12),
        "& .MuiChip-label": { fontWeight: 600 },
      })
    : undefined;

const getPlanRowActions = ({
  plan,
  locationSearch,
  navigate,
  handleNewDraft,
  handleDuplicate,
  handleDuplicateAsReusable,
  handleArchive,
  handleDeleteArchived,
  coverage,
}) => {
  const planStatus = normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "active");
  const canAssign = planStatus !== "archived";
  const actions = [
    ...(canAssign
      ? [
          {
            key: "assign",
            label: "Assign",
            onClick: () => navigate(`/home/training-plans-v2/${plan.id}/assign${locationSearch}`),
          },
        ]
      : []),
    {
      key: "duplicate",
      label: "Duplicate",
      onClick: () => handleDuplicate(plan),
    },
    ...(plan?.clientUID
      ? [
          {
            key: "duplicate_reusable",
            label: "Duplicate as reusable plan",
            onClick: () => handleDuplicateAsReusable(plan),
          },
        ]
      : []),
    {
      key: "new_draft",
      label: "New Draft",
      onClick: () => handleNewDraft(plan),
    },
    {
      key: "manage",
      label: "Manage",
      onClick: () => navigate(`/home/training-plans-v2/${plan.id}/manage${locationSearch}`),
    },
    ...(planStatus !== "archived"
      ? [
          {
            key: "archive",
            label: "Archive",
            onClick: () => handleArchive(plan),
          },
        ]
      : []),
    ...(planStatus === "archived"
      ? [
          {
            key: "delete_permanent",
            label: "Delete permanently",
            destructive: true,
            onClick: () => handleDeleteArchived(plan, coverage),
          },
        ]
      : []),
  ];
  return actions;
};

const TrainingPlansV2Library = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hints = useTrainingPlansV2Hints("library");
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [clients, setClients] = React.useState([]);
  const [lastDoc, setLastDoc] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(false);
  const [currentCursor, setCurrentCursor] = React.useState(null);
  const [cursorStack, setCursorStack] = React.useState([]);
  const [coverageByPlanId, setCoverageByPlanId] = React.useState({});
  const [stagingByPlanId, setStagingByPlanId] = React.useState({});
  const [actionsMenu, setActionsMenu] = React.useState({ anchorEl: null, planId: "" });
  /** Success / error after Duplicate / Duplicate as reusable */
  const [libraryNotice, setLibraryNotice] = React.useState(null);
  const search = searchParams.get("q") || "";
  const type = normalizeFilterValue(searchParams.get("scope"), SCOPES, "template");
  const status = normalizeFilterValue(
    searchParams.get("status"),
    VERSION_STATUS_IDS,
    DEFAULT_VERSION_STATUS,
  );
  const sort = normalizeFilterValue(searchParams.get("sort"), SORTS, "updated_desc");
  const selectedClientUID = type === "client" ? searchParams.get("clientUid") || "" : "";
  const includeInactiveClients =
    type === "client" && (searchParams.get("includeInactive") || "") === "1";

  const labelFilterIds = React.useMemo(
    () => parseLabelsQueryParam(searchParams.get("labels")),
    [searchParams],
  );
  const labelFiltersKey = labelFilterIds.join("|");

  const { labels: orgLabels, loading: labelsLoading } = useLabels();
  const selectedLabelFilters = React.useMemo(() => {
    if (!orgLabels?.length || !labelFilterIds.length) {
      return [];
    }
    const allowed = new Set(labelFilterIds.map(String));
    return orgLabels.filter((label) => label?.id && allowed.has(String(label.id)));
  }, [orgLabels, labelFiltersKey]);

  const updateFilters = React.useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const setOptionalParam = (key, value, defaultValue = "") => {
            if (!value || value === defaultValue) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          const nextScope = normalizeFilterValue(
            updates.scope ?? next.get("scope"),
            SCOPES,
            "template",
          );
          const nextStatus = normalizeFilterValue(
            updates.status ?? next.get("status"),
            VERSION_STATUS_IDS,
            DEFAULT_VERSION_STATUS,
          );
          const nextSort = normalizeFilterValue(
            updates.sort ?? next.get("sort"),
            SORTS,
            "updated_desc",
          );
          const nextQuery = updates.q ?? next.get("q") ?? "";

          setOptionalParam("scope", nextScope, "template");
          setOptionalParam("status", nextStatus, DEFAULT_VERSION_STATUS);
          setOptionalParam("sort", nextSort, "updated_desc");
          setOptionalParam("q", nextQuery, "");

          let nextLabelIds = parseLabelsQueryParam(next.get("labels"));
          if (Object.prototype.hasOwnProperty.call(updates, "labelIds")) {
            const incoming = Array.isArray(updates.labelIds) ? updates.labelIds : [];
            nextLabelIds = incoming
              .map((id) => String(id ?? "").trim())
              .filter(Boolean)
              .slice(0, MAX_LIBRARY_PROTOCOL_LABEL_FILTERS);
          }

          const labelsQueryValue = nextLabelIds.length ? Array.from(new Set(nextLabelIds.map(String))).join(",") : "";
          setOptionalParam("labels", labelsQueryValue, "");

          if (nextScope === "client") {
            const nextClientUID = updates.clientUid ?? next.get("clientUid") ?? "";
            const includeInactive =
              updates.includeInactive ?? (next.get("includeInactive") || "") === "1";
            setOptionalParam("clientUid", nextClientUID, "");
            setOptionalParam("includeInactive", includeInactive ? "1" : "", "");
          } else {
            next.delete("clientUid");
            next.delete("includeInactive");
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const loadPlans = React.useCallback(async () => {
    setLoading(true);
    const result = await listPlans({
      search,
      type,
      status,
      sort,
      clientUID: selectedClientUID,
      protocolLabelIds: labelFilterIds,
      pageSize: 25,
      startAfterDoc: currentCursor,
    });
    setPlans(result.plans || []);
    setLastDoc(result.lastDoc || null);
    setHasMore(!!result.hasMore);
    setLoading(false);
  }, [search, type, status, sort, selectedClientUID, labelFiltersKey, currentCursor]);

  React.useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  React.useEffect(() => {
    async function loadClients() {
      const value = await getAssignableClients();
      setClients(value || []);
    }
    loadClients();
  }, []);

  React.useEffect(() => {
    setCurrentCursor(null);
    setCursorStack([]);
  }, [search, type, status, sort, selectedClientUID, labelFiltersKey]);

  React.useEffect(() => {
    async function loadCoverage() {
      const planIds = plans.map((plan) => plan.id).filter(Boolean);
      if (!planIds.length || !clients.length) {
        setCoverageByPlanId({});
        return;
      }
      const coverage = await getPlanAssignmentCoverage({ planIds, clients });
      setCoverageByPlanId(coverage || {});
    }
    loadCoverage();
  }, [clients, plans]);

  React.useEffect(() => {
    let cancelled = false;
    async function loadStagingPresence() {
      const planIds = plans.map((plan) => plan.id).filter(Boolean);
      if (!planIds.length) {
        if (!cancelled) {
          setStagingByPlanId({});
        }
        return;
      }
      try {
        const presence = await getEditorStagingPresenceByPlanIds(planIds);
        if (!cancelled) {
          setStagingByPlanId(presence || {});
        }
      } catch {
        if (!cancelled) {
          setStagingByPlanId({});
        }
      }
    }
    loadStagingPresence();
    return () => {
      cancelled = true;
    };
  }, [plans]);

  const clientsById = React.useMemo(() => {
    return clients.reduce((acc, client) => {
      acc[client.uid] = client;
      return acc;
    }, {});
  }, [clients]);

  const selectedClient = React.useMemo(
    () => (selectedClientUID ? clientsById[selectedClientUID] || null : null),
    [clientsById, selectedClientUID],
  );

  const selectedClientActivePlanId = React.useMemo(
    () => selectedClient?.protocolId || "",
    [selectedClient],
  );

  const hasVisibleActivePlanMatch = React.useMemo(
    () => plans.some((plan) => plan.id === selectedClientActivePlanId),
    [plans, selectedClientActivePlanId],
  );

  const filteredClients = React.useMemo(() => {
    const activeClients = clients.filter((client) => {
      const status = (client.status || "active").toLowerCase();
      return status !== "disabled" && status !== "inactive";
    });

    const source = includeInactiveClients ? clients : activeClients;
    return [...source].sort((a, b) => {
      const aName = `${a.firstName || ""} ${a.lastName || ""}`.trim();
      const bName = `${b.firstName || ""} ${b.lastName || ""}`.trim();
      return aName.localeCompare(bName);
    });
  }, [clients, includeInactiveClients]);

  const handleDuplicate = async (plan) => {
    try {
      const result = await duplicatePlan({
        planId: plan.id,
        name: `${plan.name} (copy)`,
        clientUID: plan.clientUID || null,
      });
      await trackTrainingPlansV2Event("plan_duplicate", { planId: plan.id });
      await loadPlans();
      const dupName = result?.name || `${plan.name} (copy)`;
      setLibraryNotice({
        severity: "success",
        message: plan.clientUID
          ? `Created "${dupName}". It appears in this client’s plan list.`
          : `Created "${dupName}". It appears in this list under Reusable Plans.`,
      });
    } catch (error) {
      setLibraryNotice({
        severity: "error",
        message: error?.message || "Duplicate failed. Please try again.",
      });
    }
  };

  const handleDuplicateAsReusable = async (plan) => {
    try {
      const result = await duplicatePlanAsReusable({
        planId: plan.id,
        name: `${plan.name} (reusable copy)`,
      });
      await trackTrainingPlansV2Event("plan_duplicate_reusable", { planId: plan.id });
      await loadPlans();
      const dupName = result?.name || `${plan.name} (reusable copy)`;
      setLibraryNotice({
        severity: "success",
        message: `Created "${dupName}" as a reusable plan. It does not stay under Client plans — switch Scope to Reusable Plans to open or assign it.`,
        navigateToReusable: true,
      });
    } catch (error) {
      setLibraryNotice({
        severity: "error",
        message: error?.message || "Could not create reusable duplicate. Please try again.",
      });
    }
  };

  const handleNewDraft = async (plan) => {
    const created = await createVersionDraft({
      sourcePlanId: plan.id,
    });
    await trackTrainingPlansV2Event("plan_version_draft_create", {
      planId: plan.id,
      createdPlanId: created?.id,
      entrypoint: "training_plans_v2_library",
    });
    navigate(`/home/training-plans-v2/${created.id}/edit${location.search}`);
  };

  const handleArchive = async (plan) => {
    await setPlanStatus(plan, "archived");
    await trackTrainingPlansV2Event("plan_archive", { planId: plan.id });
    loadPlans();
  };

  const handleDeleteArchived = async (plan, coverage) => {
    const primary = coverage?.primaryCount || 0;
    const alt = coverage?.alternativeCount || 0;
    if (primary + alt > 0) {
      window.alert(
        `Cannot delete "${plan.name}" permanently while it is still assigned to clients. Change primary or alternative assignments first, then try again.`,
      );
      return;
    }
    const ok = window.confirm(
      `Permanently delete "${plan.name}"? This cannot be undone. All workouts, exercises, and version history for this plan will be removed.`,
    );
    if (!ok) {
      return;
    }
    try {
      await deleteArchivedPlanPermanently(plan.id);
      await trackTrainingPlansV2Event("plan_delete_archived_permanent", { planId: plan.id });
      loadPlans();
    } catch (err) {
      if (err?.code === "PROTOCOL_ASSIGNED") {
        window.alert(
          "This plan is still assigned to one or more clients. Change assignments before deleting.",
        );
        return;
      }
      if (err?.code === "PROTOCOL_NOT_ARCHIVED") {
        window.alert(err.message);
        return;
      }
      window.alert(err?.message || "Delete failed.");
    }
  };

  const handleOpenActionsMenu = (event, planId) => {
    setActionsMenu({ anchorEl: event.currentTarget, planId });
  };

  const handleCloseActionsMenu = () => {
    setActionsMenu({ anchorEl: null, planId: "" });
  };

  const handleRunRowAction = async (action) => {
    handleCloseActionsMenu();
    await action.onClick();
  };

  const draftPlansInView =
    status === "in_progress" ? plans.filter((p) => p.status === "draft") : [];
  const activePlansInView =
    status === "in_progress" ? plans.filter((p) => p.status === "active") : [];

  const renderPlanRow = (plan) => {
    const isSelectedClientActivePlan =
      type === "client" &&
      !!selectedClientUID &&
      !!selectedClientActivePlanId &&
      plan.id === selectedClientActivePlanId;
    return (
      <TableRow key={plan.id} hover>
        <TableCell sx={{ width: 320, maxWidth: 320 }}>
          <Typography
            fontWeight={600}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {plan.name}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              color="secondary"
              variant="outlined"
              label={`V${plan?.v2Meta?.versionNumber ?? 0}`}
            />
            <Chip
              size="small"
              label={`Status: ${plan?.v2Meta?.status || plan?.status || "active"}`}
            />
            {stagingByPlanId[plan.id] ? (
              <Tooltip title="Unpublished editor staging — open the plan to publish or revert.">
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label="Staged"
                />
              </Tooltip>
            ) : null}
            {isSelectedClientActivePlan ? (
              <CheckCircleIcon
                color="success"
                fontSize="small"
                titleAccess="Current active plan"
              />
            ) : null}
          </Stack>
        </TableCell>
        <TableCell sx={{ maxWidth: { xs: 240, md: 300 } }}>
          <Stack spacing={0.6}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
                label={getPlanTypeLabel(plan.planType)}
              />
              <Chip size="small" variant="outlined" label={plan.privacy || "private"} />
            </Stack>
            {Array.isArray(plan.protocolLabels) && plan.protocolLabels.length > 0 ? (
              <Box sx={{ opacity: 0.9 }}>
                <LabelChips
                  labels={plan.protocolLabels}
                  allLabels={orgLabels || []}
                  density="compact"
                />
              </Box>
            ) : null}
          </Stack>
        </TableCell>
        <TableCell>
          {plan.clientUID
            ? `${clientsById[plan.clientUID]?.firstName || ""} ${
                clientsById[plan.clientUID]?.lastName || ""
              }`.trim() || plan.clientUID
            : "-"}
        </TableCell>
        <TableCell>
          {(normalizeFirestoreDate(plan?.v2Meta?.versionCreatedOn) ??
            normalizeFirestoreDate(plan.createdOn) ??
            new Date()
          ).toLocaleString()}
        </TableCell>
        <TableCell>
          {(normalizeFirestoreDate(plan.updatedOn) ?? new Date()).toLocaleDateString()}
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", minWidth: 170 }}>
          <Stack direction="row" spacing={0.75} flexWrap="nowrap">
            <Chip
              size="small"
              variant="outlined"
              sx={getCoverageChipSx(coverageByPlanId[plan.id]?.primaryCount || 0, "success")}
              label={`Primary: ${coverageByPlanId[plan.id]?.primaryCount || 0}`}
            />
            <Chip
              size="small"
              variant="outlined"
              sx={getCoverageChipSx(
                coverageByPlanId[plan.id]?.alternativeCount || 0,
                "warning",
              )}
              label={`Alternative: ${coverageByPlanId[plan.id]?.alternativeCount || 0}`}
            />
          </Stack>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap", minWidth: 130 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="nowrap">
            <Button
              size="small"
              sx={{ whiteSpace: "nowrap", minWidth: "auto" }}
              onClick={() =>
                navigate(`/home/training-plans-v2/${plan.id}/edit${location.search}`)
              }
            >
              Open Editor
            </Button>
            <IconButton
              size="small"
              aria-label="More actions"
              aria-controls={
                actionsMenu.planId === plan.id ? `plan-actions-menu-${plan.id}` : undefined
              }
              aria-haspopup="true"
              aria-expanded={actionsMenu.planId === plan.id ? "true" : undefined}
              onClick={(event) => handleOpenActionsMenu(event, plan.id)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              id={`plan-actions-menu-${plan.id}`}
              anchorEl={actionsMenu.anchorEl}
              open={actionsMenu.planId === plan.id}
              onClose={handleCloseActionsMenu}
            >
              {getPlanRowActions({
                plan,
                locationSearch: location.search,
                navigate,
                handleNewDraft,
                handleDuplicate,
                handleDuplicateAsReusable,
                handleArchive,
                handleDeleteArchived,
                coverage: coverageByPlanId[plan.id],
              }).map((action) => (
                <MenuItem key={action.key} onClick={() => handleRunRowAction(action)}>
                  <ListItemText
                    primary={action.label}
                    primaryTypographyProps={action.destructive ? { color: "error" } : {}}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ pb: { xs: 10, md: 14 } }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.25, sm: 1.5 },
          mb: 1.5,
          borderRadius: 1,
          bgcolor: "background.paper",
        }}
      >
          <Stack spacing={1}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={0.5}
              alignItems={{ sm: "center" }}
              justifyContent="flex-start"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 0.06, textTransform: "uppercase", fontSize: 11 }}
              >
                Library filters
              </Typography>
              {hints.hasAnyDismissedInScope ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={hints.restoreAll}
                  sx={{ alignSelf: { xs: "flex-start", sm: "auto" }, py: 0, minHeight: 28 }}
                >
                  Show hints
                </Button>
              ) : null}
            </Stack>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
                "& > *": { minWidth: 0 },
              }}
            >
            <FormControl size="small" sx={{ flex: "0 0 auto", width: 178, maxWidth: "100%" }}>
              <InputLabel>Scope</InputLabel>
              <Select
                label="Scope"
                value={type}
                onChange={(e) => {
                  const value = e.target.value;
                  updateFilters({
                    scope: value,
                    clientUid: value === "client" ? selectedClientUID : "",
                    includeInactive: value === "client" ? includeInactiveClients : false,
                  });
                }}
              >
                <MenuItem value="template">{getPlanTypeLabel("template", "plural")}</MenuItem>
                <MenuItem value="client">Client Plans</MenuItem>
              </Select>
            </FormControl>
            {type === "client" ? (
              <Autocomplete
                size="small"
                sx={{
                  flex: "2 1 240px",
                  minWidth: { xs: "min(100%, 280px)", sm: 220 },
                  maxWidth: { lg: 440 },
                }}
                options={filteredClients}
                value={filteredClients.find((c) => c.uid === selectedClientUID) || null}
                onChange={(_, value) => updateFilters({ clientUid: value?.uid || "" })}
                isOptionEqualToValue={(option, value) => option.uid === value.uid}
                getOptionLabel={(option) =>
                  `${`${option.firstName || ""} ${option.lastName || ""}`.trim() || option.uid}${
                    option.status === "disabled" ? " (inactive)" : ""
                  }`
                }
                renderInput={(params) => (
                  <TextField {...params} label="Client (search by name)" />
                )}
              />
            ) : null}
            {type === "client" ? (
              <Tooltip title="Include inactive clients">
                <FormControlLabel
                  sx={{ flex: "0 0 auto", m: 0, alignSelf: "center", userSelect: "none" }}
                  control={
                    <Switch
                      size="small"
                      checked={includeInactiveClients}
                      onChange={(e) => updateFilters({ includeInactive: e.target.checked })}
                    />
                  }
                  label="Inactive"
                />
              </Tooltip>
            ) : null}
            <TextField
              size="small"
              label="Search by plan name"
              value={search}
              onChange={(e) => updateFilters({ q: e.target.value })}
              sx={{
                flex: "1 1 200px",
                minWidth: { xs: "min(100%, 220px)", sm: 200 },
                maxWidth: { lg: 280 },
              }}
            />
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              loading={labelsLoading}
              options={orgLabels || []}
              value={selectedLabelFilters}
              onChange={(_, value) => {
                const capped = value.slice(0, MAX_LIBRARY_PROTOCOL_LABEL_FILTERS);
                updateFilters({ labelIds: capped.map((l) => l.id) });
              }}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} size="small" checked={selected} />
                  {option?.name}
                </li>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.name} />
                ))
              }
              sx={{
                flex: "4 1 260px",
                minWidth: { xs: "min(100%, 260px)", sm: 240 },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Plan labels" placeholder="Any of…" />
              )}
            />
            <FormControl
              size="small"
              sx={{ flex: "0 1 220px", minWidth: { xs: "min(100%, 280px)", sm: 200 } }}
            >
              <InputLabel>Version Status</InputLabel>
              <Select
                label="Version Status"
                value={status}
                onChange={(e) => updateFilters({ status: e.target.value })}
              >
                <ListSubheader disableSticky sx={{ typography: "caption", lineHeight: 2 }}>
                  In progress
                </ListSubheader>
                <MenuItem value="in_progress">Active & drafts (default)</MenuItem>
                <MenuItem value="active">Active only</MenuItem>
                <MenuItem value="draft">Drafts only</MenuItem>
                <ListSubheader disableSticky sx={{ typography: "caption", lineHeight: 2 }}>
                  Archive & legacy
                </ListSubheader>
                <MenuItem value="archived">Archived</MenuItem>
                <MenuItem value="all">All statuses</MenuItem>
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ flex: "0 0 auto", width: 164, maxWidth: "100%" }}
            >
              <InputLabel>Sort</InputLabel>
              <Select
                label="Sort"
                value={sort}
                onChange={(e) => updateFilters({ sort: e.target.value })}
              >
                <MenuItem value="updated_desc">Last updated (newest)</MenuItem>
                <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                <MenuItem value="version_desc">Version (highest)</MenuItem>
                <MenuItem value="version_asc">Version (lowest)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {labelFilterIds.length > 0 && search.trim() ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -0.25 }}>
              Name search refines the labeled results on the current page (label matching is server-side; name text is applied after fetch).
            </Typography>
          ) : null}
        </Stack>
      </Paper>
      {libraryNotice ? (
        <Alert
          severity={libraryNotice.severity}
          sx={{ mb: 2 }}
          onClose={() => setLibraryNotice(null)}
          action={
            libraryNotice.navigateToReusable ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  updateFilters({ scope: "template" });
                  setLibraryNotice(null);
                }}
              >
                Reusable Plans
              </Button>
            ) : null
          }
        >
          {libraryNotice.message}
        </Alert>
      ) : null}
      {!hints.isDismissed("library_version_coverage_info") ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => hints.dismiss("library_version_coverage_info")}
        >
          Version status tracks lifecycle labels for a plan version. Assignment coverage below shows
          where plans are actively used by clients. A <strong>Staged</strong> chip means you have
          unpublished edits waiting to publish (active plans with client assignments). Draft plans
          save directly and typically will not show this chip.
        </Alert>
      ) : null}
      {!loading &&
      type === "client" &&
      !!selectedClientUID &&
      !!selectedClientActivePlanId &&
      !hasVisibleActivePlanMatch &&
      !hints.isDismissed("library_client_active_outside_list") ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => hints.dismiss("library_client_active_outside_list")}
        >
          This client&apos;s current active plan is outside this client-plan list (for example, a reusable
          template plan). Switch scope to Reusable Plans to view and manage that active assignment.
        </Alert>
      ) : null}

      {loading ? (
        <Typography>Loading plans...</Typography>
      ) : type === "client" && !selectedClientUID ? (
        !hints.isDismissed("library_select_client_prompt") ? (
          <Alert
            severity="info"
            onClose={() => hints.dismiss("library_select_client_prompt")}
          >
            Select an active client to load only that client&apos;s plans. Enable include inactive only when
            needed. Reusable plans are shown by default so the list loads faster.
          </Alert>
        ) : null
      ) : plans.length === 0 ? (
        <Box sx={{ p: 3, border: "1px dashed #ccc", borderRadius: 2 }}>
          <Typography variant="h6">No training plans yet</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              onClick={() =>
                navigate(
                  withGuidedSearchPreserve(
                    "/home/training-plans-v2/new?type=template",
                    location.search,
                  ),
                )
              }
            >
              {getPlanTypeLabel("template", "create")}
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  withGuidedSearchPreserve(
                    "/home/training-plans-v2/new?type=client",
                    location.search,
                  ),
                )
              }
            >
              {getPlanTypeLabel("client", "create")}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Type / labels</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Version Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Assignment Coverage</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "in_progress" && draftPlansInView.length > 0 ? (
              <TableRow key="section-drafts">
                <TableCell
                  colSpan={8}
                  sx={(theme) => ({
                    py: 1.25,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    borderBottom: "none",
                  })}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Drafts
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {status === "in_progress" ? draftPlansInView.map(renderPlanRow) : null}
            {status === "in_progress" && activePlansInView.length > 0 ? (
              <TableRow key="section-active">
                <TableCell
                  colSpan={8}
                  sx={(theme) => ({
                    py: 1.25,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    borderBottom: "none",
                  })}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Active
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {status === "in_progress" ? activePlansInView.map(renderPlanRow) : null}
            {status !== "in_progress" ? plans.map(renderPlanRow) : null}
          </TableBody>
        </Table>
      )}
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          disabled={loading || cursorStack.length === 0}
          onClick={() => {
            setCursorStack((prev) => {
              if (prev.length === 0) return prev;
              const next = [...prev];
              const previousCursor = next.pop();
              setCurrentCursor(previousCursor ?? null);
              return next;
            });
          }}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={loading || !hasMore || !lastDoc}
          onClick={() => {
            setCursorStack((prev) => [...prev, currentCursor]);
            setCurrentCursor(lastDoc);
          }}
        >
          Next
        </Button>
      </Stack>
    </Box>
  );
};

export default TrainingPlansV2Library;
